import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Loader2, Copy, ExternalLink, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import { initializePaddle } from "@paddle/paddle-js";
import OutcomeGuarantee from "./OutcomeGuarantee";

const PADDLE_TOKEN = process.env.REACT_APP_PADDLE_TOKEN;
const PADDLE_MONTHLY = process.env.REACT_APP_PADDLE_PRICE_ID;
const PADDLE_ANNUAL = process.env.REACT_APP_PADDLE_ANNUAL_PRICE_ID;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = "chillrefer_discovery_session_v1";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

function PreviewSkeletonCards() {
  return (
    <div className="flex flex-wrap gap-3 justify-center mt-8 opacity-60">
      {[0, 1, 2].map((i) => (
        <div key={i} className="w-[280px] rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 blur-[2px]" />
            <div className="flex-1">
              <div className="h-3 rounded bg-white/15 mb-2 blur-[1.5px]" />
              <div className="h-2.5 rounded bg-white/10 w-2/3 blur-[1.5px]" />
            </div>
          </div>
          <div className="mt-3 h-12 rounded bg-white/[0.04] blur-[1px]" />
        </div>
      ))}
    </div>
  );
}

function PersonCard({ p }) {
  const copyNote = () => {
    navigator.clipboard.writeText(p.invite_note || "");
    toast.success("Connection note copied");
  };
  return (
    <article
      data-testid="discovery-person-card"
      className="rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:border-[#FACC15]/30 transition-colors text-left"
    >
      <div className="flex items-center gap-3 mb-3">
        {p.picture_url ? (
          <img src={p.picture_url} alt={p.name} className="w-11 h-11 rounded-full object-cover" />
        ) : (
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FACC15]/30 to-purple-500/20 flex items-center justify-center text-sm font-bold text-white/70">
            {(p.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("")}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm truncate">{p.name}</p>
          <p className="text-white/45 text-xs truncate">{p.headline}</p>
        </div>
      </div>
      <p className="text-[12.5px] leading-relaxed text-white/75 bg-black/30 border border-white/5 rounded-md p-3 mb-3">
        {p.invite_note}
      </p>
      <div className="flex items-center gap-2">
        {p.profile_url && (
          <a
            href={p.profile_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="discovery-view-profile"
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-md bg-white/[0.06] border border-white/10 text-white/75 hover:text-[#FACC15] hover:border-[#FACC15]/40"
          >
            <ExternalLink size={11} /> View profile
          </a>
        )}
        <button
          onClick={copyNote}
          data-testid="discovery-copy-note"
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-md bg-[#FACC15]/10 border border-[#FACC15]/30 text-[#FACC15] hover:bg-[#FACC15]/20"
        >
          <Copy size={11} /> Copy note
        </button>
      </div>
    </article>
  );
}

function BlurredCard({ idx }) {
  return (
    <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4 select-none pointer-events-none">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-full bg-white/8 blur-[3px]" />
        <div className="flex-1">
          <div className="h-3 rounded bg-white/12 w-3/4 mb-2 blur-[2px]" />
          <div className="h-2.5 rounded bg-white/8 w-1/2 blur-[2px]" />
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="h-2 rounded bg-white/8 blur-[2px]" />
        <div className="h-2 rounded bg-white/8 blur-[2px] w-5/6" />
        <div className="h-2 rounded bg-white/8 blur-[2px] w-3/4" />
      </div>
    </div>
  );
}

export default function LandingHero() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // {session_id, job, visible[], locked[], total_found}
  const [showPricing, setShowPricing] = useState(false);
  const [plan, setPlan] = useState("annual"); // "monthly" | "annual"
  const [paddle, setPaddle] = useState(null);
  const resultsRef = useRef(null);
  const pricingRef = useRef(null);

  // Init Paddle once
  useEffect(() => {
    if (!PADDLE_TOKEN) return;
    initializePaddle({ environment: "production", token: PADDLE_TOKEN })
      .then((inst) => inst && setPaddle(inst))
      .catch((e) => console.error("Paddle init failed", e));
  }, []);

  const search = async () => {
    setError("");
    const trimmed = (url || "").trim();
    if (!/linkedin\.com\/jobs\/view\/\d+/i.test(trimmed) && !/currentJobId=\d+/i.test(trimmed)) {
      setError("Paste a LinkedIn job URL — like https://www.linkedin.com/jobs/view/3856781234");
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/discovery/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: trimmed }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data?.detail || "Search failed. Please try again.");
        setLoading(false);
        return;
      }
      setResult(data);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ session_id: data.session_id, job: data.job }));
      } catch {}
      setLoading(false);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch (e) {
      setError("Network error. Try again in a moment.");
      setLoading(false);
    }
  };

  const goPricing = () => {
    setShowPricing(true);
    setTimeout(() => pricingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const startCheckout = () => {
    if (!paddle) {
      toast.error("Checkout is loading — try again in a second.");
      return;
    }
    const priceId = plan === "annual" ? PADDLE_ANNUAL : PADDLE_MONTHLY;
    if (!priceId) {
      toast.error("Checkout is not configured. Please reach out at support@chillrefer.com.");
      return;
    }
    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customData: { discovery_session_id: result?.session_id, source: "discovery_funnel" },
      settings: {
        displayMode: "overlay",
        theme: "dark",
        locale: "en",
        successUrl: `${window.location.origin}/onboard?session=${result?.session_id || ""}&from_paddle=1`,
      },
      successCallback: () => {
        window.location.href = `/onboard?session=${result?.session_id || ""}&from_paddle=1`;
      },
    });
  };

  return (
    <section className="pt-12 pb-12" id="hero">
      <div className="max-w-[1100px] mx-auto px-6 text-center">
        <motion.p initial="hidden" animate="visible" variants={fadeUp}
          className="text-white/40 text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold mb-5">
          For people that want to stop cold applying to jobs
        </motion.p>

        <motion.h1 initial="hidden" animate="visible" variants={fadeUp}
          className="hero-headline text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-5">
          Paste a job link. See exactly <span className="underline decoration-[#FACC15] decoration-4 underline-offset-8">who can refer you</span> inside the company.
        </motion.h1>

        <motion.p initial="hidden" animate="visible" variants={fadeUp}
          className="text-white/55 text-base sm:text-lg max-w-[680px] mx-auto leading-relaxed mb-8">
          AI finds the hiring managers, recruiters, and employees most likely to refer you — and writes a personalised connection note for each one. No signup required.
        </motion.p>

        {/* Input */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp}
          className="max-w-[680px] mx-auto flex flex-col sm:flex-row gap-3">
          <input
            type="url"
            data-testid="discovery-url-input"
            placeholder="Paste a LinkedIn job URL — linkedin.com/jobs/view/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !loading && search()}
            className="flex-1 px-5 py-4 rounded-md bg-white/[0.04] border border-white/15 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#FACC15]/60 focus:bg-white/[0.06] transition-all"
          />
          <button
            onClick={search}
            disabled={loading}
            data-testid="discovery-search-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-[#FACC15] text-black font-black text-sm hover:bg-[#fde047] disabled:opacity-60 disabled:cursor-not-allowed transition-all hover:scale-[1.02]"
          >
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /> Searching…</>
            ) : (
              <>Find who to contact <ArrowRight size={14} /></>
            )}
          </button>
        </motion.div>
        <p className="text-[11px] text-white/35 mt-3">No signup required · Results in 30 seconds · Free for the first 3 searches</p>

        <div className="mt-5 flex justify-center">
          <OutcomeGuarantee variant="hero" />
        </div>

        {error && (
          <p className="mt-4 text-red-400/90 text-xs" data-testid="discovery-error">{error}</p>
        )}

        {/* Pre-search teaser */}
        {!result && !loading && (
          <>
            <PreviewSkeletonCards />
            <p className="text-[11px] text-white/30 mt-4">↑ Try with a real LinkedIn job link — we'll show 3 real referrers + their personalised notes.</p>
          </>
        )}

        {/* Results */}
        <AnimatePresence>
          {result && (
            <motion.div
              ref={resultsRef}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-12"
              data-testid="discovery-results"
            >
              <div className="text-left max-w-[920px] mx-auto">
                <p className="text-[11px] tracking-wider uppercase text-[#FACC15] mb-2">
                  <Sparkles size={12} className="inline mr-1" /> {result.job.company} · {result.job.title}
                </p>
                <h2 className="text-2xl sm:text-3xl font-black mb-2">
                  {result.total_found} {result.total_found === 1 ? "person who can refer you" : "people who can refer you"}
                  <span className="text-white/40 font-bold"> for this {result.job.title} role</span>
                </h2>
                <p className="text-white/45 text-sm mb-7">
                  Personalised connection notes ready for each one. Click "View profile" to open their LinkedIn, or "Copy note" to send manually.
                </p>

                {/* Visible 3 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-7">
                  {result.visible.map((p, i) => <PersonCard key={i} p={p} />)}
                </div>

                {/* Blurred cards behind paywall */}
                {result.locked.length > 0 && (
                  <div className="relative">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-h-[480px] overflow-hidden">
                      {result.locked.slice(0, 9).map((_, i) => <BlurredCard key={i} idx={i} />)}
                    </div>
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/95 to-transparent flex flex-col items-center justify-end pb-6">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FACC15]/10 border border-[#FACC15]/30 text-[11px] text-[#FACC15] font-semibold mb-3">
                        <Lock size={11} /> {result.locked.length} more hiring managers found — unlock with ChillRefer
                      </div>
                      <button
                        onClick={goPricing}
                        data-testid="discovery-cta-unlock"
                        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-[#FACC15] text-black font-black text-sm hover:bg-[#fde047] hover:scale-[1.02] transition-all"
                      >
                        Automate outreach to all {result.total_found} hiring managers tonight <ArrowRight size={14} />
                      </button>
                      <p className="text-[11px] text-white/45 mt-3 max-w-[520px] text-center leading-relaxed px-4">
                        ChillRefer connects to your LinkedIn and sends personalised connection requests to all {result.total_found} people tonight. Auto-follows up if they accept. Up to 600 hiring managers per month — without you lifting a finger.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Inline pricing */}
        <AnimatePresence>
          {showPricing && (
            <motion.div
              ref={pricingRef}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-14 max-w-[760px] mx-auto"
              data-testid="discovery-pricing"
            >
              <h2 className="text-3xl sm:text-4xl font-black mb-3">Start tonight — first outreach runs within 24h</h2>
              <p className="text-white/50 text-sm mb-7 max-w-[520px] mx-auto">Cancel anytime. Outcome Guarantee. Pay securely with card or PayPal through Paddle.</p>
              <div className="grid sm:grid-cols-2 gap-4 mb-6 text-left">
                <button
                  onClick={() => setPlan("monthly")}
                  data-testid="discovery-plan-monthly"
                  className={`rounded-2xl border-2 p-6 transition-all ${plan === "monthly" ? "border-[#FACC15] bg-[#FACC15]/5" : "border-white/15 bg-white/[0.02] hover:border-white/25"}`}
                >
                  <p className="text-xs uppercase tracking-wider text-white/40 mb-1">Monthly</p>
                  <p className="text-4xl font-black mb-1">$99<span className="text-base font-semibold text-white/50">/mo</span></p>
                  <p className="text-white/45 text-xs">Cancel anytime</p>
                  {plan === "monthly" && (
                    <p className="text-[11px] text-[#FACC15] mt-3 inline-flex items-center gap-1"><Check size={11}/>Selected</p>
                  )}
                </button>
                <button
                  onClick={() => setPlan("annual")}
                  data-testid="discovery-plan-annual"
                  className={`relative rounded-2xl border-2 p-6 transition-all ${plan === "annual" ? "border-[#FACC15] bg-[#FACC15]/5" : "border-white/15 bg-white/[0.02] hover:border-white/25"}`}
                >
                  <span className="absolute -top-2.5 right-4 text-[10px] uppercase tracking-wider bg-[#FACC15] text-black font-black px-2 py-0.5 rounded">Best value · save $389</span>
                  <p className="text-xs uppercase tracking-wider text-white/40 mb-1">Annual</p>
                  <p className="text-4xl font-black mb-1">$799<span className="text-base font-semibold text-white/50">/yr</span></p>
                  <p className="text-white/45 text-xs">$66/mo equivalent · 33% off</p>
                  {plan === "annual" && (
                    <p className="text-[11px] text-[#FACC15] mt-3 inline-flex items-center gap-1"><Check size={11}/>Selected</p>
                  )}
                </button>
              </div>
              <button
                onClick={startCheckout}
                data-testid="discovery-pay-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md bg-[#FACC15] text-black font-black text-sm hover:bg-[#fde047] hover:scale-[1.02] transition-all"
              >
                Start tonight — pay securely with card <ArrowRight size={14} />
              </button>
              <p className="text-[11px] text-white/40 mt-3">After paying, connect your LinkedIn in one click. First outreach runs tonight.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
